import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  termLabel,
  type FacultyMember,
  type InstitutionCourse,
  type InstitutionOverview,
} from "../api.js";

/**
 * The university admin's home.
 *
 * They are accountable for a lab programme they do not teach: their question is
 * "is every course running, and is anything going wrong", not "what is my next
 * assignment". So this leads with the institution's shape and the handful of
 * states that quietly break a semester, and only then lists the courses.
 */
export default function Institution() {
  const [overview, setOverview] = useState<InstitutionOverview | null>(null);
  const [courses, setCourses] = useState<InstitutionCourse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [status, setStatus] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [faculty, setFaculty] = useState<FacultyMember[]>([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    year: new Date().getFullYear(),
    semester: 1,
    teacherId: "",
  });
  const [reload, setReload] = useState(0);

  const loadOverview = () =>
    api
      .get<InstitutionOverview>("/api/institution/overview")
      .then(setOverview)
      .catch((e) => setError(e.message));

  useEffect(() => {
    void loadOverview();
    api
      .get<{ faculty: FacultyMember[] }>("/api/admin/faculty")
      .then((r) => setFaculty(r.faculty.filter((f) => f.active)))
      .catch(() => {});
  }, []);

  /**
   * Hand a course to a teacher, from the row that reports it has none.
   *
   * This console's whole job is to find the states that quietly break a
   * semester, and "no teacher enrolled" was one it could name and not fix --
   * the only moment a teacher could be assigned was course creation, so a
   * course whose teacher had left stayed broken while being listed as broken.
   */
  async function assignTeacher(courseId: string, teacherId: string) {
    if (!teacherId) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await api.patch(`/api/courses/${courseId}`, { teacherId });
      const who = faculty.find((f) => f.id === teacherId);
      setStatus(`${who?.name ?? "The teacher"} now runs this course.`);
      setReload((n) => n + 1);
      void loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not assign the teacher.");
    } finally {
      setBusy(false);
    }
  }

  async function createCourse(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const r = await api.post<{ course: { code: string }; teacher: { name: string } }>(
        "/api/courses",
        {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          year: form.year,
          semester: form.semester,
          ...(form.teacherId ? { teacherId: form.teacherId } : {}),
        }
      );
      setStatus(`${r.course.code} created — ${r.teacher.name} teaches it.`);
      setForm({ code: "", name: "", year: new Date().getFullYear(), semester: 1, teacherId: "" });
      setShowNew(false);
      setReload((n) => n + 1);
      void loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the course.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams();
    if (showArchived) params.set("includeArchived", "true");
    if (term) {
      const [year, semester] = term.split("-");
      params.set("year", year);
      params.set("semester", semester);
    }
    if (search.trim()) params.set("search", search.trim());
    const qs = params.toString();
    api
      .get<{ courses: InstitutionCourse[] }>(`/api/institution/courses${qs ? `?${qs}` : ""}`)
      .then((r) => setCourses(r.courses))
      .catch((e) => setError(e.message));
  }, [showArchived, term, search, reload]);

  // Terms come from the courses themselves; an institution's calendar is
  // whatever it has actually run.
  const terms = Array.from(
    new Map(courses.map((c) => [`${c.year}-${c.semester}`, c])).values()
  ).map((c) => ({ key: `${c.year}-${c.semester}`, label: termLabel(c) }));

  const counts = overview?.counts;
  const attention = overview?.attention;
  const hasAttention =
    attention &&
    (attention.withoutTeacher.length > 0 ||
      attention.withoutContent.length > 0 ||
      attention.unbatchedStudents > 0);

  return (
    <div className="page">
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Institution</h1>
          <div className="muted">Every course, and how it is going</div>
        </div>
        <div className="row">
          <Link to="/monitor"><button>Exams &amp; marking</button></Link>
          <Link to="/faculty"><button>Faculty</button></Link>
          <Link to="/audit"><button>Audit log</button></Link>
          <button className="primary" onClick={() => setShowNew((v) => !v)}>
            {showNew ? "Cancel" : "New course"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      {showNew && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>NEW COURSE</div>
          <form onSubmit={createCourse}>
            <div className="grid-2">
              <label>
                <span>Course ID / code</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="CS1101"
                  required
                  autoFocus
                />
              </label>
              <label>
                <span>Course name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Problem Solving and Programming in C"
                  required
                />
              </label>
            </div>
            <div className="grid-2">
              <label>
                <span>Year</span>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                  required
                />
              </label>
              <label>
                <span>Semester</span>
                <select
                  value={form.semester}
                  onChange={(e) => setForm({ ...form, semester: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                    <option key={s} value={s}>Semester {s}</option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              <span>Teacher</span>
              <select
                value={form.teacherId}
                onChange={(e) => setForm({ ...form, teacherId: e.target.value })}
              >
                <option value="">Me — I will teach it</option>
                {faculty.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} · {f.email}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              The teacher you name is enrolled to run it — they add the assignments, exams and
              roster. You set up the offering; you do not have to teach it.
            </p>
            <button className="primary" disabled={busy}>
              {busy ? "Creating…" : "Create course"}
            </button>
          </form>
        </div>
      )}

      {counts && (
        <div className="card">
          <div className="stats">
            <Stat value={counts.courses} label="active courses" />
            <Stat value={counts.teachers} label="teachers" />
            <Stat value={counts.students} label="students" />
            <Stat
              value={counts.liveExams}
              label="exams open now"
              highlight={counts.liveExams > 0}
            />
            <Stat value={counts.attemptsInProgress} label="sitting right now" />
            <Stat value={counts.submissionsToday} label="submissions today" />
            <Stat value={counts.unreviewed} label="awaiting review" />
          </div>
        </div>
      )}

      {hasAttention && (
        <div className="notice">
          <strong>Needs attention.</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {attention.withoutTeacher.length > 0 && (
              <li>
                No teacher enrolled:{" "}
                {attention.withoutTeacher.map((c) => `${c.code} (${termLabel(c)})`).join(", ")}
              </li>
            )}
            {attention.withoutContent.length > 0 && (
              <li>
                No assignments or exams yet:{" "}
                {attention.withoutContent.map((c) => `${c.code} (${termLabel(c)})`).join(", ")}
              </li>
            )}
            {attention.unbatchedStudents > 0 && (
              <li>
                {attention.unbatchedStudents} student
                {attention.unbatchedStudents === 1 ? " is" : "s are"} not assigned to a lab batch —
                they cannot be scheduled separately for an exam.
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Courses ({courses.length})</strong>
          <div className="row">
            <input
              style={{ width: 220 }}
              placeholder="Search code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select style={{ width: "auto" }} value={term} onChange={(e) => setTerm(e.target.value)}>
              <option value="">All terms</option>
              {terms.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              <span className="muted" style={{ marginBottom: 0, fontSize: 13 }}>Archived</span>
            </label>
          </div>
        </div>

        {courses.length === 0 ? (
          <div className="muted">No courses match.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Term</th>
                <th>Teachers</th>
                <th>Students</th>
                <th>Work</th>
                <th>Awaiting review</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.code}</strong>
                    {c.archived && <span className="badge draft" style={{ marginLeft: 6 }}>archived</span>}
                  </td>
                  <td>{c.name}</td>
                  <td className="muted">{termLabel(c)}</td>
                  <td>
                    {c.teachers.length === 0 ? (
                      // Assigning from here rather than sending the admin to a
                      // teacher's page: they are looking at the list of
                      // unstaffed courses, and staffing them is the task.
                      <select
                        defaultValue=""
                        disabled={busy || c.archived}
                        onChange={(e) => void assignTeacher(c.id, e.target.value)}
                      >
                        <option value="">— assign a teacher —</option>
                        {faculty.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">
                        {c.teachers.map((t) => t.name).join(", ")}
                      </span>
                    )}
                  </td>
                  <td className="muted">{c.studentCount}</td>
                  <td className="muted">
                    {c.assignmentCount} assignment{c.assignmentCount === 1 ? "" : "s"}
                    {c.examCount > 0 && ` · ${c.examCount} exam${c.examCount === 1 ? "" : "s"}`}
                  </td>
                  <td>
                    {c.unreviewedCount > 0 ? (
                      <span className="badge warn">{c.unreviewedCount}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/institution/courses/${c.id}`}><button>Open</button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  highlight,
}: {
  value: number;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className="stat">
      <div className="value" style={highlight ? { color: "var(--warn)" } : undefined}>
        {value}
      </div>
      <div className="label">{label}</div>
    </div>
  );
}
